import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Heading,
  Divider,
  VStack,
  Flex,
  Button,
  useToast,
  ButtonGroup,
  HStack,
  Text,
} from '@chakra-ui/react';

import axios from 'axios';
import { AvatarInput } from '../../../../../components/Form/AvatarInput';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { createSpotsService } from '../../../../../services/Spots/CreateSpotsService';
import { Switch } from '../../../../../components/Form/Switch';
import { translateError } from '../../../../../utils/errors';
import { updateSpotAvatarsService } from '../../../../../services/Spots/UpdateSpotAvatarsService';
import { useAuth } from '../../../../../hooks/auth';

type NewSpotFormData = {
  description?: string;
  isActive: boolean;
  isLivingArea: boolean;
  name: string;
  ventureId: string;
};

const registerSpotFormSchema = Yup.object().shape({
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  isActive: Yup.boolean().required('Requerido'),
  isLivingArea: Yup.boolean().required('Requerido'),
  name: Yup.string().required('Requerido'),
  ventureId: Yup.string().uuid().required('Requerido'),
});

export const SpotRegister = (): JSX.Element => {
  const { venture: authenticatedVenture } = useAuth();
  const { goBack, push } = useHistory();
  const toast = useToast();

  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();

  const { register, handleSubmit, formState, setValue, watch } = useForm({
    resolver: yupResolver(registerSpotFormSchema),
  });

  const { errors } = formState;

  const isLivingAreaSelected = watch('isLivingArea');

  useEffect(() => {
    setValue('ventureId', authenticatedVenture?.id);
  }, [setValue, authenticatedVenture]);

  const handleChangeAvatar = useCallback((file: File) => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  }, []);

  const handleDeleteAvatar = useCallback(() => {
    setAvatar(undefined);
    setAvatarUrl(undefined);
  }, []);

  const handleNewSpot: SubmitHandler<NewSpotFormData> = useCallback(
    async ({ description, isActive, isLivingArea, name, ventureId }) => {
      try {
        const spot = await createSpotsService({
          description,
          isActive,
          isLivingArea,
          name,
          ventureId,
        });

        if (avatar) {
          const formData = new FormData();

          formData.append('avatar', avatar);

          await updateSpotAvatarsService({
            avatarData: formData,
            spotId: spot.id,
          });
        }

        toast({
          title: 'Cadastrado com sucesso',
          description: 'O local foi cadastrado corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/spots/details', {
          spotId: spot.id,
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha no cadastro',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o local, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [avatar, push, toast],
  );

  return (
    <DefaultLayout>
      <Box
        as="form"
        flex="1"
        borderRadius={8}
        bg="white"
        p="8"
        onSubmit={handleSubmit(handleNewSpot)}
      >
        <Heading size="lg" fontWeight="normal">
          Cadastrar local
        </Heading>

        <Divider my="6" borderColor="gray.300" />

        <Flex justify="center" mb="8">
          <AvatarInput
            avatarUrl={avatarUrl}
            onChange={handleChangeAvatar}
            onDelete={handleDeleteAvatar}
          />
        </Flex>

        <VStack spacing="8">
          <MaskedInput
            label="Título"
            error={errors.name}
            {...register('name')}
          />

          <MaskedInput
            label="Descrição"
            as="textarea"
            minHeight="160px"
            resize="none"
            py="2"
            error={errors.description}
            {...register('description')}
          />

          <Flex w="full" direction="column" align="center">
            <HStack align="flex-end">
              <Switch
                label="Área de convivência"
                error={errors.isLivingArea}
                {...register('isLivingArea')}
              />

              <Switch
                label="Ativo"
                error={errors.isActive}
                defaultChecked
                {...register('isActive')}
              />
            </HStack>

            {isLivingAreaSelected && (
              <>
                <Text fontSize="sm" color="gray.500" mt="4">
                  Não será validado nível de experiência para reservas em
                  eventos de atividades nesse local
                </Text>

                <Text fontSize="sm" color="gray.500" mt="4">
                  Será considerado limite de reservas grátis para convidados em
                  eventos de atividades nesse local
                </Text>
              </>
            )}
          </Flex>
        </VStack>

        <Flex mt="12" justify="flex-end">
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={goBack}>
              Cancelar
            </Button>
            <Button
              type="submit"
              colorScheme="green"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
    </DefaultLayout>
  );
};
